import { Draggable } from '@hello-pangea/dnd';
import { Collapse } from '@mui/material';
import cx from 'classnames';
import { setExpandedRecipe, setLegacyMode, setRecipeGroups } from 'js/actions';
import Tooltip from 'js/components/elements/mui/Tooltip';
import { RECIPE_GROUP_TRANSFORMS, TransformParameterTypes } from 'js/constants/schemaEditor';
import { GROUPED_TRANSFORM_RULES } from 'js/constants/transforms';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import { completeTxBuilderRecipeGroups } from 'js/lib/RecipeBuilderUtils';
import { sanitizeApiKey } from 'js/lib/URLUtils';
import { forEach, isArray, isEmpty, map, reduce } from 'lodash';
import { DraggableVerticalList } from 'new/components/DraggableVerticalList/DraggableVerticalList';
import { ReactComponent as Burger } from 'new/components/icons/burger.svg';
import { ReactComponent as EyeIcon } from 'new/components/icons/eye.svg';
import { ReactComponent as EyeSlashIcon } from 'new/components/icons/fi-rr-eye-crossed.svg';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-rr-plus.svg';
import { RemoveIconButton } from 'new/elements/Button/RemoveIconButton';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { reorder } from 'new/utils';
import React from 'react';
import { connect } from 'react-redux';
import UIUtils from '../../lib/UIUtils';
import UserConfirmation from '../route_utils/UserConfirmation';
import LoadableTransformDSLEditor from './LoadableTransformDSLEditor';
import TransformRecipe from './TransformRecipe';
import s from './TransformRecipeGroup.module.scss';

class TransformRecipeGroup extends React.PureComponent {
  state = {
    // whether the mode switch will result in loss of data due to invalid rules
    isSwitchLossy: false,
  };

  render() {
    const { expandedRecipeIndex, isReadOnly, isExpanded, isLegacyMode } = this.props as any;
    const { isSwitchLossy } = this.state;
    const btnStyle = isExpanded ? { padding: 12, marginBottom: -12 } : {};

    return (
      <>
        <div className="schema-validations-editor">
          <Collapse in={isExpanded}>{this._renderTransformGroup()}</Collapse>
          {!isReadOnly && (
            <div className={s.bottom_buttons}>
              <button
                style={btnStyle}
                className="add-new-transform-button"
                onClick={this._handleAddTransformRecipeClicked}
              >
                <PlusIcon width={8} height={8} />
                Add New Transform{' '}
                {!isLegacyMode && isExpanded && expandedRecipeIndex !== -1 && (
                  <span className="hotkey">(Shift-Enter)</span>
                )}
              </button>
              {isExpanded && (
                <button
                  style={btnStyle}
                  className={cx('add-new-transform-button', s.legacy_mode_toggle)}
                  onClick={this._handleToggleMode}
                >
                  {isLegacyMode ? 'Switch to Advanced Editor' : 'Switch to Classic Editor'}
                </button>
              )}
            </div>
          )}
        </div>
        {isSwitchLossy && (
          <UserConfirmation
            message="Some transformation rules are incomplete or invalid. Switching modes may cause these rules to be lost. Do you want to continue?"
            cancelButtonLabel="Cancel"
            okButtonLabel="Continue"
            onCancel={this._handleModeChangeCanceled}
            onConfirm={this._handleModeChangeConfirmed}
          />
        )}
      </>
    );
  }

  _renderTransformGroup = () => {
    const { data, isLegacyMode } = this.props as any;

    return (
      <DraggableVerticalList
        items={data}
        listClassname="filters-list"
        onDragEnd={this._handleDragEnd}
        renderDraggableList={isLegacyMode ? this._renderLegacyModeList : this._renderDSLModeList}
      />
    );
  };

  _renderDSLModeList = (list) => {
    const { expandedRecipeIndex, parsingErrors, isReadOnly, isInteractive, onApplyRecipe } = this
      .props as any;

    return list.map(({ __id, code, disabled }, index) => {
      const isDisabled = disabled === true;

      return (
        <Draggable index={index} key={__id} draggableId={__id} isDragDisabled={isReadOnly}>
          {(provided) => (
            <li
              id={__id}
              ref={provided.innerRef}
              {...provided.draggableProps}
              className={cx('recipe-group-element filter-item', s.card, {
                'is-read-only': isReadOnly && !isInteractive,
                'is-disabled': isDisabled,
                'has-errors': !!parsingErrors[code],
              })}
            >
              {/* @ts-ignore */}
              <Tooltip tooltipTheme="fieldInfo" title={parsingErrors[code] || undefined}>
                <div className={cx('left', s.transform_recipe_card)}>
                  <div className={s.header}>
                    <div
                      className="schema-validation-rule-description"
                      style={{ width: 'calc(100% - 48px)' }}
                    >
                      <div {...provided.dragHandleProps}>
                        <TransformItemHeader index={index} isReadOnly={isReadOnly} />
                      </div>
                      <LoadableTransformDSLEditor
                        value={code}
                        isReadOnly={isReadOnly}
                        index={index}
                        expandedRecipeIndex={expandedRecipeIndex}
                        onRun={onApplyRecipe}
                        onNewRule={this._handleAddTransformRecipeClicked}
                        onChange={(newValue) => this._handleTransformCodeChanged(newValue, index)}
                        onFocusRule={this._handleFocusRule}
                        onNavigateRules={this._handleNavigateRules}
                        onBlurOutside={this._handleBlurOutsideEditor}
                      />
                    </div>
                    {!isReadOnly && (
                      <div className={s.actions}>
                        {expandedRecipeIndex !== -1 &&
                          Math.abs(index - expandedRecipeIndex) === 1 && (
                            <div className={cx(s.adjacent_editor_hotkey, 'search-bar-hotkey')}>
                              {navigator.platform.includes('Mac') ? 'Cmd-' : 'Ctrl-'}
                              {index < expandedRecipeIndex ? '[' : ']'}
                            </div>
                          )}
                        {/* @ts-ignore */}
                        <Tooltip title={isDisabled ? 'Enable Rule' : 'Disable Rule'}>
                          <IconButton
                            onClick={() => {
                              this._handleDisableAttributeClicked(index);
                            }}
                          >
                            {isDisabled ? (
                              <EyeSlashIcon width={16} height={16} />
                            ) : (
                              <EyeIcon width={16} height={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <RemoveIconButton
                          onClickRemove={() => {
                            this._handleDeleteAttributeClicked(index);
                          }}
                          tooltipText="Remove Rule"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Tooltip>
            </li>
          )}
        </Draggable>
      );
    });
  };

  _renderLegacyModeList = (list) => {
    const {
      dataSet,
      expandedRecipeIndex,
      isReadOnly,
      isInteractive,
      onRecipeClicked,
      onLoadReusableTransforms,
    } = this.props as any;

    return list.map((item, index) => {
      const { index: recipeIndex } = item;
      const isExpanded = !isReadOnly && index === expandedRecipeIndex;
      const isDisabled = item.disabled === true;

      const handleExpandOnClick = (e) => {
        if (isReadOnly && isInteractive) {
          onRecipeClicked(recipeIndex);
          return;
        }

        this._handleExpandClicked(e, index);
      };

      const expandedRuleTileClassname = isExpanded ? 'expanded-rule-title' : 'collapsed-rule-title';

      return (
        <Draggable
          index={index}
          key={item.__id}
          draggableId={item.__id}
          isDragDisabled={isExpanded || isReadOnly}
        >
          {(provided) => {
            return (
              <li
                id={item.__id}
                ref={provided.innerRef}
                {...provided.draggableProps}
                className={cx('recipe-group-element filter-item', s.card, s.legacy_mode_card, {
                  [s.expanded_card_bg]: isExpanded,
                  [s.no_expanded_card_bg]: !isExpanded,
                  'is-focused': isExpanded,
                  'is-blurred': !isExpanded,
                  'is-read-only': isReadOnly && !isInteractive,
                  'is-disabled': isDisabled,
                })}
              >
                <div className={cx('left', s.transform_recipe_card)}>
                  <div onClick={handleExpandOnClick} className={s.header}>
                    <div className="schema-validation-rule-description">
                      <div {...provided.dragHandleProps}>
                        <TransformItemHeader index={index} isReadOnly={isReadOnly} />
                      </div>
                      <span className={cx(expandedRuleTileClassname, s.rule_description)}>
                        {this._renderRule(item, true, isExpanded)}
                      </span>
                    </div>

                    {!isReadOnly && (
                      <div className={s.actions}>
                        {/* @ts-ignore */}
                        <Tooltip title={isDisabled ? 'Enable Rule' : 'Disable Rule'}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              this._handleDisableAttributeClicked(index);
                            }}
                          >
                            {isDisabled ? (
                              <EyeSlashIcon width={16} height={16} />
                            ) : (
                              <EyeIcon width={16} height={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <RemoveIconButton
                          onClickRemove={(e) => {
                            e.stopPropagation();
                            this._handleDeleteAttributeClicked(index);
                          }}
                          tooltipText="Remove Rule"
                        />
                      </div>
                    )}
                  </div>

                  {isExpanded ? (
                    <div className={s.body}>
                      <TransformRecipe
                        dataSet={dataSet}
                        transformData={item}
                        onLoadReusableTransforms={onLoadReusableTransforms}
                        onChangeTransformData={(newTransformData) =>
                          this._handleTransformChanged(newTransformData, index)
                        }
                      />
                    </div>
                  ) : null}
                </div>
              </li>
            );
          }}
        </Draggable>
      );
    });
  };

  _renderRule = (transformData, renderDest = false, isExpandedMode = false) => {
    const { transforms, attributeTransformsLoaded } = this.props as any;
    const { name, parameters, parentPath, __destAttr } = transformData;
    const isAttributeTransform = name?.startsWith('customAttrTx_');
    let rule;

    if (isAttributeTransform) {
      if (!attributeTransformsLoaded) {
        return <span className="invalid-rule">Loading transform...</span>;
      }

      const transformId = parameters?.attrTxId?.value;
      const transform = transforms[transformId];

      rule = {
        label: transform?.name || name,
        params: parameters?.attrTxId?.argValues,
      };
    } else {
      rule = GROUPED_TRANSFORM_RULES.rules[name];
    }

    if (!rule) {
      return <span className="invalid-rule">Unrecognized transform</span>;
    }

    const { label, ignoreDest, params: ruleParams, requiredParams, __srcAttr } = rule;

    // required param verification
    let destPath;
    let isIncomplete = false;

    if (renderDest) {
      const destPathParts = parentPath ? parentPath.split('.') : [];

      if (__destAttr) {
        destPathParts.push(__destAttr);
      }

      destPath = reduce(
        destPathParts,
        (acc, pathPart, index: number) => {
          const className = isExpandedMode ? 'expanded-rule-title' : 'attr-name';
          acc.push(
            <span key={index} className={className}>
              {pathPart}
            </span>,
          );

          if (index < destPathParts.length - 1) {
            acc.push('.');
          }

          return acc;
        },
        [],
      );

      if (!ignoreDest && isEmpty(destPath)) {
        isIncomplete = true;
      }
    }

    forEach(requiredParams, (paramName) => {
      const value = parameters[paramName];

      if (
        (value?.type === TransformParameterTypes.ATTR_REF ||
          value?.type === TransformParameterTypes.SYS_ATTR_REF) &&
        !value?.name
      ) {
        isIncomplete = true;

        return false;
      }
    });

    if (isIncomplete) {
      const className = isExpandedMode ? 'expanded-rule-title' : 'invalid-rule';
      return <span className={className}>Incomplete transform rule</span>;
    }

    // render params
    let params = [];

    if (__srcAttr && parameters.__srcAttr && !parameters.__srcAttr.__ignoreSrc) {
      params.push(this._renderParam(parameters.__srcAttr, rule.__srcAttr));
    }

    params.push(
      ...map(ruleParams, (ruleParam) =>
        isAttributeTransform
          ? this._renderParam(ruleParam)
          : parameters[ruleParam.name]
            ? this._renderParam(parameters[ruleParam.name], ruleParam)
            : 'null',
      ),
    );

    return (
      <>
        {renderDest && (
          <>
            {destPath}
            {!isEmpty(destPath) && ' = '}
          </>
        )}
        <span className="attr-name function-name">{label}</span>
        {params.length ? (
          <>
            {' ( '}
            {UIUtils.renderAndJoin(
              params,
              (param, index) => (
                <span key={index}>{param}</span>
              ),
              ' , ',
              ' , ',
            )}
            {' ) '}
          </>
        ) : null}
      </>
    );
  };

  _renderParamAndFindRule = (param) => {
    return this._renderParam(
      param,
      param.type === TransformParameterTypes.FUNCTION
        ? GROUPED_TRANSFORM_RULES.rules[param.name]
        : null,
    );
  };

  _renderParam = (param, rule = null) => {
    if (isArray(param)) {
      // render objects as JSON ({key1: value1, key2: value2})
      if (rule?.inputElement === 'object') {
        const entries = [];

        for (let index = 0; index < param.length; index += 2) {
          const objectKey = this._renderParamAndFindRule(param[index]);
          const objectValue = this._renderParamAndFindRule(param[index + 1]);

          entries.push(
            <span key={`key-${index}`}>{objectKey}</span>,
            ' : ',
            <span key={`value-${index}`}>{objectValue}</span>,
          );

          if (index < param.length - 2) {
            entries.push(' , ');
          }
        }

        return (
          <>
            {' { '}
            {entries}
            {' } '}
          </>
        );
      }

      // render array elements in square brackets
      return (
        <>
          {' [ '}
          {UIUtils.renderAndJoin(
            param,
            (element, index) => (
              <span key={index}>{this._renderParamAndFindRule(element)}</span>
            ),
            ' , ',
            ' , ',
          )}
          {' ] '}
        </>
      );
    }

    if (
      param.type === TransformParameterTypes.ATTR_REF ||
      param.type === TransformParameterTypes.SYS_ATTR_REF
    ) {
      return <span className="attr-name">{param.name}</span>;
    }

    if (param.type === TransformParameterTypes.FUNCTION) {
      return this._renderRule(param);
    }

    if (rule?.name === 'apiKey') {
      return <span className="attr-name literal-value">{sanitizeApiKey(param.value)}</span>;
    }

    return <span className="attr-name literal-value">{param.value}</span>;
  };

  _handleAddTransformRecipeClicked = () => {
    const { data, isExpanded, isLegacyMode, setExpandedRecipe, onExpand, onChangeRecipeGroup } =
      this.props as any;
    const newData = [
      ...data,
      isLegacyMode
        ? {
            __id: crypto.randomUUID(),
            type: TransformParameterTypes.FUNCTION,
            name: 'eqAttr',
            parameters: {
              __srcAttr: {
                type: TransformParameterTypes.ATTR_REF,
                name: '',
              },
            },
            __destAttr: '',
          }
        : {
            __id: crypto.randomUUID(),
            code: '',
          },
    ];

    if (!isExpanded) {
      onExpand();
    }

    onChangeRecipeGroup(newData);
    setExpandedRecipe(newData.length - 1);
  };

  _handleTransformChanged = (transformData, index) => {
    const { data, onChangeRecipeGroup } = this.props as any;
    const newData = [...data];
    newData[index] = transformData;

    onChangeRecipeGroup(newData);
  };

  _handleTransformCodeChanged = (code, index) => {
    const { data } = this.props as any;
    const transformData = {
      ...data[index],
      code,
    };

    this._handleTransformChanged(transformData, index);
  };

  _handleDisableAttributeClicked = (index) => {
    const { data, onChangeRecipeGroup } = this.props as any;
    const newData = [...data];

    // toggle 'disabled' if it exists; otherwise, set it to true
    newData[index] = {
      ...newData[index],
      disabled: 'disabled' in newData[index] ? !newData[index].disabled : true,
    };

    onChangeRecipeGroup(newData);
  };

  _handleDeleteAttributeClicked = (index) => {
    const { data, setExpandedRecipe, onChangeRecipeGroup } = this.props as any;
    const newData = [...data];
    newData.splice(index, 1);

    onChangeRecipeGroup(newData);
    setExpandedRecipe(-1);
  };

  _handleExpandClicked = (_event, index) => {
    const { expandedRecipeIndex, setExpandedRecipe } = this.props as any;
    setExpandedRecipe(expandedRecipeIndex === index ? -1 : index);
  };

  _handleFocusRule = (index) => {
    const { expandedRecipeIndex, setExpandedRecipe } = this.props as any;
    if (expandedRecipeIndex !== index) {
      setExpandedRecipe(index);
    }
  };

  _handleNavigateRules = (direction) => {
    const { data, expandedRecipeIndex, setExpandedRecipe } = this.props as any;
    const newIndex = expandedRecipeIndex + direction;

    if (newIndex >= 0 && newIndex < data.length) {
      setExpandedRecipe(newIndex);
    }
  };

  _handleBlurOutsideEditor = () => {
    const { isExpanded, setExpandedRecipe } = this.props as any;

    if (isExpanded) {
      setExpandedRecipe(-1);
    }
  };

  _handleDragEnd = (response) => {
    const { source, destination } = response;
    const { data, onChangeRecipeGroup } = this.props as any;

    if (typeof destination?.index === 'undefined') return;

    onChangeRecipeGroup(reorder(data, source?.index, destination?.index));
  };

  _handleToggleMode = (event = null, stopOnError = true) => {
    const { recipeGroups, transforms, setRecipeGroups, setLegacyMode, isLegacyMode } = this
      .props as any;
    let newRecipeGroups = recipeGroups;

    // if switching from legacy to DSL, remove the code from each recipe and
    // have it generated by completeTxBuilderRecipeGroups later
    if (isLegacyMode) {
      newRecipeGroups = newRecipeGroups.map((recipeGroup) => {
        const { type, data } = recipeGroup;

        if (type !== RECIPE_GROUP_TRANSFORMS) {
          return recipeGroup;
        }

        // remove the code from each recipe
        const newData = data.map((recipe) => ({
          ...recipe,
          code: undefined,
        }));

        return {
          ...recipeGroup,
          data: newData,
        };
      });
      AnalyticsUtils.captureCustomEvent('nexset_designer_dsl_mode_switch');
    } else {
      AnalyticsUtils.captureCustomEvent('nexset_designer_legacy_mode_switch');
    }

    // try to parse/stringify the rules
    try {
      newRecipeGroups = completeTxBuilderRecipeGroups(newRecipeGroups, transforms, stopOnError);
    } catch (error) {
      if (stopOnError) {
        this.setState({ isSwitchLossy: true });
        return;
      }
    }

    // if no errors, switch the mode
    setRecipeGroups(newRecipeGroups);
    setLegacyMode(!isLegacyMode);
  };

  _handleModeChangeCanceled = () => {
    this.setState({ isSwitchLossy: false });
  };

  _handleModeChangeConfirmed = () => {
    this.setState({ isSwitchLossy: false });
    this._handleToggleMode(null, false);
  };
}

const TransformItemHeader = ({
  index,
  isReadOnly = false,
}: {
  index: number;
  isReadOnly: boolean;
}) => {
  return (
    <div className={cx(s.icon_container, { [s.read_only]: isReadOnly })}>
      <Burger className={s.burger_icon} />
      <div className={s.sequence_number}>{index + 1}</div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    entities: { transforms },
    recipeBuilder: { recipeGroups, expandedRecipeIndex, parsingErrors, isLegacyMode },
    transforms: {
      attributeTransforms: { lastUpdated },
    },
  } = state;

  return {
    transforms,
    recipeGroups,
    expandedRecipeIndex,
    attributeTransformsLoaded: !!lastUpdated,
    parsingErrors,
    isLegacyMode,
  };
};

export default connect(mapStateToProps, {
  setRecipeGroups,
  setExpandedRecipe,
  setLegacyMode,
})(TransformRecipeGroup);
